import { 
  XBRLFormInput, 
  XBRLPresentationRole,
  XBRLPresentationConcept,
  XBRLLabel,
  XBRLHypercubeData,
  XBRLHypercubeRole
} from '../schema/xbrl-types';
import { 
  XBRLFormSchema, 
  FormSection, 
  FormField, 
  FormColumn,
  ConceptTree,
  FieldType 
} from '../schema/types';

/**
 * XBRL Form B  private static assignColumnIdsToConceptFields(conceptTree: ConceptTree, columnIds: string[]): void {
    // Filter fields to only keep those that match available columns
    conceptTree.fields = conceptTree.fields.filter(field => {
      return columnIds.includes(field.columnId);
    });

    // Recursively process children
    if (conceptTree.children) {
      conceptTree.children.forEach(child => {
        this.assignColumnIdsToConceptFields(child, columnIds);
      });
    }
  }ransforms XBRL taxonomy data into Jupiter Dynamic Form schemas
 * Updated to handle actual presentation.json structure with roles
 */
export class XBRLFormBuilder {
  
  /**
   * Create unique concept ID by combining original ID with preferred label suffix
   * This handles cases where the same concept appears multiple times with different labels
   */
  private static createUniqueConceptId(concept: XBRLPresentationConcept, sectionId?: string): string {
    let baseId = concept.id;
    
    // Add preferred label suffix if exists
    if (concept.preferredLabel && concept.preferredLabel.trim() !== '') {
      // Extract the last part of the role URL as suffix
      const roleMatch = concept.preferredLabel.match(/\/([^\/]+)$/);
      const roleSuffix = roleMatch ? roleMatch[1] : concept.preferredLabel.replace(/[^a-zA-Z0-9]/g, '_');
      baseId = `${concept.id}_${roleSuffix}`;
    }
    
    // Add section context to avoid conflicts across roles
    if (sectionId) {
      // Create a short hash of the section ID to keep concept IDs manageable
      const sectionHash = this.createSectionHash(sectionId);
      return `${baseId}__${sectionHash}`;
    }
    
    return baseId;
  }
  
  /**
   * Create a short hash from section ID for concept uniqueness
   */
  private static createSectionHash(sectionId: string): string {
    // Simple hash to create a short identifier from the section ID
    let hash = 0;
    for (let i = 0; i < sectionId.length; i++) {
      const char = sectionId.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36).substr(0, 6);
  }
  
  /**
   * Build form schema from XBRL input data
   * Creates accordion sections for each presentation role
   */
  static buildFormSchema(
    xbrlInput: XBRLFormInput, 
    periodStartDate?: string, 
    periodEndDate?: string
  ): XBRLFormSchema {
    if (!xbrlInput.presentation || xbrlInput.presentation.length === 0) {
      throw new Error('XBRL presentation data is required');
    }

    // Use the first entrypoint (typically there's only one)
    const presentationData = xbrlInput.presentation[0];
    
    // Extract hypercube data for dimension processing
    const hypercubeData = xbrlInput.hypercubes?.[0]; // Use first entrypoint
    
    const sections: FormSection[] = [];

    // Sort roles by ascending order of their title before processing
    const sortedRoles = [...presentationData.roles].sort((a, b) => {
      return a.role.localeCompare(b.role);
    });

    // Create a form section for each presentation role
    sortedRoles.forEach(role => {
      const section = this.buildSectionFromRole(role, periodStartDate, periodEndDate, hypercubeData);
      
      // Post-process fields to assign correct column IDs based on generated columns
      this.assignFieldColumnIds(section);
      
      sections.push(section);
    });

    return {
      formId: `xbrl-form-${Date.now()}`,
      title: `XBRL Form - ${this.extractEntityName(presentationData.entrypoint)}`,
      description: `Dynamic form generated from XBRL taxonomy data`,
      version: '1.0.0',
      sections: sections
    };
  }

  /**
   * Build a form section from a presentation role
   */
  private static buildSectionFromRole(
    role: XBRLPresentationRole, 
    periodStartDate?: string, 
    periodEndDate?: string,
    hypercubeData?: XBRLHypercubeData
  ): FormSection {
    // Extract section title from role name (remove role number prefix)
    const title = this.extractRoleTitle(role.role);
    
    // Analyze period types in this role first to determine consistent column strategy
    const nonAbstractConcepts = this.getAllNonAbstractConcepts(role);
    const periodTypes = new Set(
      nonAbstractConcepts
        .filter(concept => concept.periodType)
        .map(concept => concept.periodType)
    );

    // Find matching hypercube role for dimension processing
    const hypercubeRole = hypercubeData?.roles.find(hr => hr.roleId === role.id);
    
    if (hypercubeRole) {
      //console.log(`🏷️ Found matching hypercube role "${role.id}"`);
    } else {
      //console.log(`❌ No hypercube role found for "${role.id}"`);
    }
    
    // Generate appropriate columns based on concept period types and hypercube dimensions FIRST
    const columns = this.generateDefaultColumnsForRole(role, periodStartDate || '2025-01-01', periodEndDate || '2025-12-31', hypercubeRole, nonAbstractConcepts, periodTypes);

    // Extract column IDs for field creation
    const availableColumnIds = columns.map(col => col.id);
    //console.log(`📋 Available column IDs for role "${role.id}":`, availableColumnIds);
    

    
    const roleInfo = { periodTypes, availableColumnIds };
    const conceptTrees: ConceptTree[] = [];

    // Process root concepts and build tree structure with knowledge of available columns
    if (role.presentationLinkbase?.concepts) {
      role.presentationLinkbase.concepts.forEach(concept => {
        const conceptTree = this.buildConceptTree(concept, 0, periodStartDate, periodEndDate, roleInfo, role.id);
        if (conceptTree) {
          conceptTrees.push(conceptTree);
        }
      });
    }

    return {
      id: role.id,
      title: title,
      description: role.description || `Section for ${title}`,
      concepts: conceptTrees,
      columns: columns,
      expanded: false,
      metadata: {
        roleURI: role.roleURI,
        originalRole: role.role
      }
    };
  }

  /**
   * Build concept tree from XBRL presentation concept
   */
  private static buildConceptTree(
    concept: XBRLPresentationConcept, 
    level: number, 
    periodStartDate?: string, 
    periodEndDate?: string,
    roleInfo?: { periodTypes: Set<string>, availableColumnIds?: string[] },
    sectionId?: string
  ): ConceptTree | null {
    const label = this.getPreferredLabel(concept.labels);
    
    // Build fields for this concept (if not abstract)
    const fields: FormField[] = [];
    if (!concept.elementAbstract) {
      // Use available column IDs if provided, otherwise fall back to period-based logic
      let columnIds: string[] = [];
      
      if (roleInfo?.availableColumnIds && roleInfo.availableColumnIds.length > 0) {
        // Multi-dimensional or complex column scenario - filter by concept's period type
        columnIds = roleInfo.availableColumnIds.filter(colId => {
          if (concept.periodType === 'instant') {
            return colId.includes('instant') || colId === 'instant';
          } else if (concept.periodType === 'duration') {
            return colId.includes('duration') || colId === 'duration';
          } else {
            // If concept has no period type, include all columns
            return true;
          }
        });
        
        // If no columns match the period type, fall back to all available columns
        if (columnIds.length === 0) {
          columnIds = roleInfo.availableColumnIds;
        }
      } else {
        // Fallback to original period-based logic
        if (!roleInfo?.periodTypes || roleInfo.periodTypes.size === 0) {
          columnIds = ['default'];
        } else if (roleInfo.periodTypes.size === 1 && roleInfo.periodTypes.has('instant')) {
          columnIds = ['instant'];
        } else if (roleInfo.periodTypes.size === 1 && roleInfo.periodTypes.has('duration')) {
          columnIds = ['duration'];
        } else {
          // Mixed period types - create fields for both duration and instant columns
          // based on the concept's own period type
          if (concept.periodType === 'instant') {
            columnIds = ['instant'];
          } else if (concept.periodType === 'duration') {
            columnIds = ['duration'];
          } else {
            // Fallback if period type is not specified
            columnIds = ['duration', 'instant'];
          }
        }
      }
      
      // Create fields for appropriate column IDs
      columnIds.forEach(columnId => {
        const field = this.createFieldFromConcept(concept, periodStartDate, periodEndDate, columnId);
        if (field) fields.push(field);
      });
    }

    // Process children recursively
    const children: ConceptTree[] = [];
    if (concept.children && concept.children.length > 0) {
      concept.children.forEach(child => {
        const childTree = this.buildConceptTree(child, level + 1, periodStartDate, periodEndDate, roleInfo, sectionId);
        if (childTree) {
          children.push(childTree);
        }
      });
    }

    // Check if this concept should be included in the tree
    // For abstract concepts: only include if they have fields OR have children with content
    // For non-abstract concepts: always include if they have fields
    const hasContent = fields.length > 0 || children.length > 0;
    
    if (!hasContent) {
      // Abstract concept with no fields and no children with content - omit from tree
      return null;
    }

    return {
      id: this.createUniqueConceptId(concept, sectionId), // Use unique ID for form management
      originalConceptId: concept.id, // Store original for submission data
      name: concept.conceptName,
      label: label,
      description: `${concept.conceptName} (${concept.type})`,
      level: level,
      children: children,
      fields: fields,
      collapsed: level > 0, // Collapse nested levels by default
      abstract: concept.elementAbstract,
      periodType: concept.periodType as 'instant' | 'duration'
    };
  }

  /**
   * Create form field from XBRL concept
   */
  private static createFieldFromConcept(
    concept: XBRLPresentationConcept, 
    periodStartDate?: string, 
    periodEndDate?: string,
    forcedColumnId?: string
  ): FormField | null {
    const fieldType = this.mapXBRLTypeToFieldType(concept.type);
    const label = this.getPreferredLabel(concept.labels);

    // Determine appropriate column ID based on concept's period type or forced value
    let columnId: string;
    
    if (forcedColumnId) {
      // Use the forced column ID if provided
      columnId = forcedColumnId;
    } else {
      // Determine column ID based on period type
      if (concept.periodType === 'instant') {
        columnId = 'instant';
      } else if (concept.periodType === 'duration') {
        columnId = 'duration'; // Single column for duration
      } else {
        columnId = 'default';
      }
    }    const field: FormField = {
      id: `${concept.id}_${columnId}_field`,
      conceptId: concept.id,
      columnId: columnId,
      type: fieldType,
      label: label,
      placeholder: `Enter ${label.toLowerCase()}`,
      required: false, // Will be determined by validation rules
      disabled: concept.elementAbstract,
      defaultValue: null
    };

    // Add period information if the concept is period-specific
    if (concept.periodType === 'duration' || concept.periodType === 'instant') {
      field.periodStartDate = periodStartDate || '2025-01-01';
      field.periodEndDate = periodEndDate || '2025-12-31';
    }

    return field;
  }

  /**
   * Map XBRL data type to form field type
   */
  private static mapXBRLTypeToFieldType(xbrlType: string): FieldType {
    const typeMap: Record<string, FieldType> = {
      'xbrli:stringItemType': 'text',
      'xbrli:monetaryItemType': 'currency',
      'xbrli:integerItemType': 'number',
      'xbrli:decimalItemType': 'decimal',
      'xbrli:percentItemType': 'percentage',
      'xbrli:dateItemType': 'date',
      'xbrli:booleanItemType': 'boolean',
      'dtr-types:domainItemType': 'select',
      'bw2-titel9:chamberOfCommerceRegistrationNumberItemType': 'text'
    };

    return typeMap[xbrlType] || 'text';
  }

  /**
   * Get preferred label from labels array
   */
  private static getPreferredLabel(labels: XBRLLabel[]): string {
    if (!labels || labels.length === 0) return 'Unnamed Concept';

    // Try to find preferred label
    const preferred = labels.find(l => l.preferredLabel);
    if (preferred) return preferred.label;

    // Try to find standard label
    const standard = labels.find(l => l.role === 'http://www.xbrl.org/2003/role/label');
    if (standard) return standard.label;

    // Try to find terse label
    const terse = labels.find(l => l.role === 'http://www.xbrl.org/2003/role/terseLabel');
    if (terse) return terse.label;

    // Return first available label
    return labels[0].label;
  }

  /**
   * Assign appropriate column IDs to fields based on section's generated columns
   */
  private static assignFieldColumnIds(section: FormSection): void {
    if (!section.columns || section.columns.length === 0) {
      return;
    }

    // Get available column IDs
    const columnIds = section.columns.map(col => col.id);
    
    // Process all fields in all concept trees
    section.concepts.forEach(conceptTree => {
      this.assignColumnIdsToConceptFields(conceptTree, columnIds);
    });
  }

  /**
   * Recursively assign column IDs to fields in a concept tree
   */
  private static assignColumnIdsToConceptFields(conceptTree: ConceptTree, columnIds: string[]): void {
    // Filter fields to only keep those that match available columns
    conceptTree.fields = conceptTree.fields.filter(field => {
      return columnIds.includes(field.columnId);
    });

    // Process children recursively
    if (conceptTree.children) {
      conceptTree.children.forEach(child => {
        this.assignColumnIdsToConceptFields(child, columnIds);
      });
    }
  }

  /**
   * Count total fields in a concept tree recursively
   */
  private static countFieldsInTree(conceptTree: ConceptTree): number {
    let count = conceptTree.fields.length;
    if (conceptTree.children) {
      conceptTree.children.forEach(child => {
        count += this.countFieldsInTree(child);
      });
    }
    return count;
  }

  /**
   * Get all non-abstract concepts from a role recursively
   */
  static getAllNonAbstractConcepts(role: XBRLPresentationRole): XBRLPresentationConcept[] {
    const nonAbstractConcepts: XBRLPresentationConcept[] = [];
    
    if (role.presentationLinkbase?.concepts) {
      role.presentationLinkbase.concepts.forEach(concept => {
        this.collectNonAbstractConcepts(concept, nonAbstractConcepts);
      });
    }
    
    return nonAbstractConcepts;
  }

  /**
   * Recursively collect non-abstract concepts from a concept tree
   */
  private static collectNonAbstractConcepts(
    concept: XBRLPresentationConcept, 
    collection: XBRLPresentationConcept[]
  ): void {
    // Add this concept if it's not abstract
    if (!concept.elementAbstract) {
      collection.push(concept);
    }
    
    // Recursively process children
    if (concept.children && concept.children.length > 0) {
      concept.children.forEach(child => {
        this.collectNonAbstractConcepts(child, collection);
      });
    }
  }

  /**
   * Generate default columns based on period types of non-abstract concepts in a role
   */
  static generateDefaultColumnsForRole(
    role: XBRLPresentationRole, 
    periodStartDate: string, 
    periodEndDate: string,
    hypercubeRole?: XBRLHypercubeRole,
    nonAbstractConcepts?: XBRLPresentationConcept[],
    rolePeriodTypes?: Set<string>
  ): FormColumn[] {
    // Use pre-calculated values if provided, otherwise calculate them
    const concepts = nonAbstractConcepts || this.getAllNonAbstractConcepts(role);
    const periodTypes = rolePeriodTypes || new Set(
      concepts
        .filter(concept => concept.periodType) // Only concepts with period type
        .map(concept => concept.periodType)
    );
    
    //console.log(`📊 Analyzing role "${role.role}" with ${concepts.length} non-abstract concepts`);
    
    if (concepts.length === 0) {
      // No non-abstract concepts, return single default column
      //console.log('📊 No non-abstract concepts found, using default column');
      return [{
        id: 'default',
        title: 'Value',
        description: 'Default value column',
        type: 'base',
        order: 0,
        removable: false
      }];
    }
    
    // Check for hypercube dimensions and generate appropriate columns
    let dimensionColumns: FormColumn[] = [];
    if (hypercubeRole?.items?.length === 1) {
      const item = hypercubeRole.items[0];
      
      
      if (item.dimensions.length === 1) {
        // Single dimension - existing logic
        dimensionColumns = this.generateSingleDimensionColumns(item.dimensions[0], periodStartDate, periodEndDate, periodTypes);
      } else if (item.dimensions.length > 1) {
        // Multiple dimensions - generate combination columns
        dimensionColumns = this.generateMultiDimensionColumns(item.dimensions, periodStartDate, periodEndDate, periodTypes);
      }
    } else if (hypercubeRole?.items?.length && hypercubeRole.items.length > 1) {
      //console.log(`⚠️ Multiple items found (${hypercubeRole.items.length}), skipping for now`);
    }
    
    // Analyze period types (use the pre-calculated or newly calculated set)
    //console.log(`📅 Found period types: ${Array.from(periodTypes).join(', ')}`);
    
    const columns: FormColumn[] = [];
    
    // If we have dimension columns, use them
    if (dimensionColumns.length > 0) {
      columns.push(...dimensionColumns);
    } else {
      // Fallback to period-based columns
      if (periodTypes.size === 0) {
        // No period types specified, use single default column
        //console.log('📅 No period types found, using default column');
        columns.push({
          id: 'default',
          title: 'Value',
          description: 'Default value column',
          type: 'base',
          order: 0,
          removable: false
        });
      } else if (periodTypes.size === 1 && periodTypes.has('instant')) {
        // All concepts are instant - single column with start date
        //console.log('📅 All concepts are instant type, creating single column');
        columns.push({
          id: 'instant',
          title: `Current`,
          description: `Values as of ${periodStartDate}`,
          type: 'base',
          order: 0,
          removable: false
        });
      } else if (periodTypes.size === 1 && periodTypes.has('duration')) {
        // All concepts are duration - single column with period range
        //console.log('📅 All concepts are duration type, creating single column with period range');
        columns.push({
          id: 'duration',
          title: `Current Period`,
          description: `Period: ${periodStartDate} to ${periodEndDate}`,
          type: 'base',
          order: 0,
          removable: false
        });
      } else {
        // Mixed period types - create both duration and instant columns
        //console.log('📅 Mixed period types found, creating both duration and instant columns');
        
        // Duration column with period range
        columns.push({
          id: 'duration',
          title: `Current Period`,
          description: `Period: ${periodStartDate} to ${periodEndDate}`,
          type: 'base',
          order: 0,
          removable: false
        });
        
        // Instant column using periodStartDate as the instant date
        columns.push({
          id: 'instant',
          title: `Current`,
          description: `Values as of ${periodStartDate}`,
          type: 'base',
          order: 1,
          removable: false
        });
      }
    }
    
    //console.log(`📊 Generated ${columns.length} columns for role "${role.role}":`, columns.map(c => c.title));
    return columns;
  }

  /**
   * Generate columns for single dimension scenarios
   */
  private static generateSingleDimensionColumns(
    dimension: any, 
    periodStartDate: string, 
    periodEndDate: string, 
    periodTypes: Set<string>
  ): FormColumn[] {
    // //console.log(`📊 Processing single dimension:`, {
    //   id: dimension.id,
    //   conceptName: dimension.conceptName,
    //   hasMembers: !!(dimension.members && Array.isArray(dimension.members)),
    //   membersCount: dimension.members?.length || 0,
    //   hasTypedMember: !!dimension.typedMember,
    //   typedMemberId: dimension.typedMember?.id
    // });

    // Handle typed dimensions (no explicit members)
    if (dimension.typedMember && (!dimension.members || dimension.members.length === 0)) {
      //console.log(`🔤 Processing typed dimension: ${dimension.id} with typed member: ${dimension.typedMember.id}`);
      
      const axisLabel = dimension.labels.find((l: any) => l.role === 'http://www.xbrl.org/2003/role/label')?.label || dimension.conceptName;
      
      const dimensionInfo = {
        axisId: dimension.id,
        axisLabel,
        typedMemberId: dimension.typedMember.id,
        dimensionKey: `${axisLabel} | [Typed Input]`,
        dimensionIdKey: `${dimension.id}|[typed]`
      };
      
      //console.log(`🔤 Generated typed dimension info:`, dimensionInfo);
      
      const columns: FormColumn[] = [];
      
      if (periodTypes.size === 0) {
        columns.push({
          id: 'typed-default',
          title: axisLabel,
          description: 'Typed dimension input column',
          type: 'dimension',
          dimensionData: {
            dimensionId: 'typed-default',
            axisId: dimensionInfo.axisId,
            memberId: '[typed]',
            memberValue: 'Typed Input',
            memberLabel: 'Typed Input',
            axis: dimensionInfo.axisLabel,
            axisLabel: dimensionInfo.axisLabel,
            memberKey: dimensionInfo.dimensionKey,
            dimensionIdKey: dimensionInfo.dimensionIdKey,
            hasTypedMembers: true,
            typedMemberId: dimensionInfo.typedMemberId,
            typedMembers: [{
              axisId: dimensionInfo.axisId,
              axisLabel: dimensionInfo.axisLabel,
              typedMemberId: dimensionInfo.typedMemberId,
              memberLabel: dimensionInfo.axisLabel
            }]
          },
          order: 0,
          removable: false
        });
      } else if (periodTypes.size === 1 && periodTypes.has('instant')) {
        columns.push({
          id: 'typed-instant',
          title: `${axisLabel} [Typed Input Available]`,
          description: `Typed values as of ${periodStartDate}`,
          type: 'dimension',
          dimensionData: {
            dimensionId: 'typed-instant',
            axisId: dimensionInfo.axisId,
            memberId: '[typed]',
            memberValue: 'Typed Input',
            memberLabel: 'Typed Input',
            axis: dimensionInfo.axisLabel,
            axisLabel: dimensionInfo.axisLabel,
            memberKey: dimensionInfo.dimensionKey,
            dimensionIdKey: dimensionInfo.dimensionIdKey,
            hasTypedMembers: true,
            typedMemberId: dimensionInfo.typedMemberId,
            typedMembers: [{
              axisId: dimensionInfo.axisId,
              axisLabel: dimensionInfo.axisLabel,
              typedMemberId: dimensionInfo.typedMemberId,
              memberLabel: dimensionInfo.axisLabel
            }]
          },
          order: 0,
          removable: false
        });
      } else if (periodTypes.size === 1 && periodTypes.has('duration')) {
        columns.push({
          id: 'typed-duration',
          title: `${axisLabel} [Typed Input Available]`,
          description: `Typed values for period ${periodStartDate} to ${periodEndDate}`,
          type: 'dimension',
          dimensionData: {
            dimensionId: 'typed-duration',
            axisId: dimensionInfo.axisId,
            memberId: '[typed]',
            memberValue: 'Typed Input',
            memberLabel: 'Typed Input',
            axis: dimensionInfo.axisLabel,
            axisLabel: dimensionInfo.axisLabel,
            memberKey: dimensionInfo.dimensionKey,
            dimensionIdKey: dimensionInfo.dimensionIdKey,
            hasTypedMembers: true,
            typedMemberId: dimensionInfo.typedMemberId,
            typedMembers: [{
              axisId: dimensionInfo.axisId,
              axisLabel: dimensionInfo.axisLabel,
              typedMemberId: dimensionInfo.typedMemberId,
              memberLabel: dimensionInfo.axisLabel
            }]
          },
          order: 0,
          removable: false
        });
      } else {
        // Mixed period types - create both instant and duration columns
        columns.push({
          id: 'typed-duration',
          title: `${axisLabel} [Typed Input Available]`,
          description: `Typed values for period ${periodStartDate} to ${periodEndDate}`,
          type: 'dimension',
          dimensionData: {
            dimensionId: 'typed-duration',
            axisId: dimensionInfo.axisId,
            memberId: '[typed]',
            memberValue: 'Typed Input',
            memberLabel: 'Typed Input',
            axis: dimensionInfo.axisLabel,
            axisLabel: dimensionInfo.axisLabel,
            memberKey: dimensionInfo.dimensionKey,
            dimensionIdKey: dimensionInfo.dimensionIdKey,
            hasTypedMembers: true,
            typedMemberId: dimensionInfo.typedMemberId,
            typedMembers: [{
              axisId: dimensionInfo.axisId,
              axisLabel: dimensionInfo.axisLabel,
              typedMemberId: dimensionInfo.typedMemberId,
              memberLabel: dimensionInfo.axisLabel
            }]
          },
          order: 0,
          removable: false
        });
        
        columns.push({
          id: 'typed-instant',
          title: `${axisLabel} [Typed Input Available]`,
          description: `Typed values as of ${periodStartDate}`,
          type: 'dimension',
          dimensionData: {
            dimensionId: 'typed-instant',
            axisId: dimensionInfo.axisId,
            memberId: '[typed]',
            memberValue: 'Typed Input',
            memberLabel: 'Typed Input',
            axis: dimensionInfo.axisLabel,
            axisLabel: dimensionInfo.axisLabel,
            memberKey: dimensionInfo.dimensionKey,
            dimensionIdKey: dimensionInfo.dimensionIdKey,
            hasTypedMembers: true,
            typedMemberId: dimensionInfo.typedMemberId,
            typedMembers: [{
              axisId: dimensionInfo.axisId,
              axisLabel: dimensionInfo.axisLabel,
              typedMemberId: dimensionInfo.typedMemberId,
              memberLabel: dimensionInfo.axisLabel
            }]
          },
          order: 1,
          removable: false
        });
      }
      
      //console.log(`🔤 Generated ${columns.length} typed dimension columns`);
      return columns;
    }

    // Handle explicit dimensions (existing logic)
    if (!dimension.members || !Array.isArray(dimension.members)) {
      //console.log(`⚠️ Dimension ${dimension.id} has no members defined and no typed member, skipping column generation`);
      return [];
    }

    // //console.log(`📊 Found single dimension:`, {
    //   id: dimension.id,
    //   conceptName: dimension.conceptName,
    //   membersCount: dimension.members.length
    // });
    
    if (dimension.members.length === 0) {
      return [];
    }
    
    const axisLabel = dimension.labels.find((l: any) => l.role === 'http://www.xbrl.org/2003/role/label')?.label || dimension.conceptName;
    const firstMember = dimension.members[0];
    const memberLabel = firstMember.labels.find((l: any) => l.role === 'http://www.xbrl.org/2003/role/label')?.label || firstMember.conceptName;
    
    const dimensionInfo = {
      axisId: dimension.id,
      axisLabel,
      memberId: firstMember.id,
      memberLabel,
      dimensionKey: `${axisLabel} | ${memberLabel}`,
      dimensionIdKey: `${dimension.id} | ${firstMember.id}`
    };
    
    //console.log(`📊 Generated dimension info:`, dimensionInfo);
    
    const columns: FormColumn[] = [];
    
    if (periodTypes.size === 0) {
      columns.push({
        id: 'default',
        title: dimensionInfo.memberLabel,
        description: 'Default value column',
        type: 'dimension',
        dimensionData: {
          dimensionId: 'default',
          axisId: dimensionInfo.axisId,
          memberId: dimensionInfo.memberId,
          memberValue: dimensionInfo.memberLabel,
          memberLabel: dimensionInfo.memberLabel,
          axis: dimensionInfo.axisLabel,
          axisLabel: dimensionInfo.axisLabel,
          memberKey: dimensionInfo.dimensionKey,
          dimensionIdKey: dimensionInfo.dimensionIdKey
        },
        order: 0,
        removable: false
      });
    } else if (periodTypes.size === 1 && periodTypes.has('instant')) {
      columns.push({
        id: 'instant',
        title: `${dimensionInfo.memberLabel}`,
        description: `Values as of ${periodStartDate}`,
        type: 'dimension',
        dimensionData: {
          dimensionId: 'instant',
          axisId: dimensionInfo.axisId,
          memberId: dimensionInfo.memberId,
          memberValue: dimensionInfo.memberLabel,
          memberLabel: dimensionInfo.memberLabel,
          axis: dimensionInfo.axisLabel,
          axisLabel: dimensionInfo.axisLabel,
          memberKey: dimensionInfo.dimensionKey,
          dimensionIdKey: dimensionInfo.dimensionIdKey
        },
        order: 0,
        removable: false
      });
    } else if (periodTypes.size === 1 && periodTypes.has('duration')) {
      columns.push({
        id: 'duration',
        title: `${dimensionInfo.memberLabel}`,
        description: `Period: ${periodStartDate} to ${periodEndDate}`,
        type: 'dimension',
        dimensionData: {
          dimensionId: 'duration',
          axisId: dimensionInfo.axisId,
          memberId: dimensionInfo.memberId,
          memberValue: dimensionInfo.memberLabel,
          memberLabel: dimensionInfo.memberLabel,
          axis: dimensionInfo.axisLabel,
          axisLabel: dimensionInfo.axisLabel,
          memberKey: dimensionInfo.dimensionKey,
          dimensionIdKey: dimensionInfo.dimensionIdKey
        },
        order: 0,
        removable: false
      });
    } else {
      // Mixed period types - create both duration and instant columns
      columns.push({
        id: 'duration',
        title: `${dimensionInfo.memberLabel}`,
        description: `Period: ${periodStartDate} to ${periodEndDate}`,
        type: 'dimension',
        dimensionData: {
          dimensionId: 'duration',
          axisId: dimensionInfo.axisId,
          memberId: dimensionInfo.memberId,
          memberValue: dimensionInfo.memberLabel,
          memberLabel: dimensionInfo.memberLabel,
          axis: dimensionInfo.axisLabel,
          axisLabel: dimensionInfo.axisLabel,
          memberKey: dimensionInfo.dimensionKey,
          dimensionIdKey: dimensionInfo.dimensionIdKey
        },
        order: 0,
        removable: false
      });
      
      columns.push({
        id: 'instant',
        title: `${dimensionInfo.memberLabel}`,
        description: `Values as of ${periodStartDate}`,
        type: 'dimension',
        dimensionData: {
          dimensionId: 'instant',
          axisId: dimensionInfo.axisId,
          memberId: dimensionInfo.memberId,
          memberValue: dimensionInfo.memberLabel,
          memberLabel: dimensionInfo.memberLabel,
          axis: dimensionInfo.axisLabel,
          axisLabel: dimensionInfo.axisLabel,
          memberKey: dimensionInfo.dimensionKey,
          dimensionIdKey: dimensionInfo.dimensionIdKey
        },
        order: 1,
        removable: false
      });
    }
    
    return columns;
  }

  /**
   * Generate columns for multi-dimension scenarios - creates combinations of all dimension members
   */
  private static generateMultiDimensionColumns(
    dimensions: any[], 
    periodStartDate: string, 
    periodEndDate: string, 
    periodTypes: Set<string>
  ): FormColumn[] {
    //console.log(`📊 Processing ${dimensions.length} dimensions for multi-dimensional columns`);
    // console.log(`🔍 [DEBUG] Input dimensions:`, dimensions.map(d => ({
    //   id: d.id,
    //   label: d.labels?.find((l: any) => l.role === 'http://www.xbrl.org/2003/role/label')?.label,
    //   membersCount: d.members?.length || 0,
    //   hasTypedMember: !!d.typedMember
    // })));
    
    // Detect typed members for header indication
    const hasTypedMembers = dimensions.some(dimension => dimension.typedMember);
    if (hasTypedMembers) {
      //console.log(`🔤 Found typed members in dimensions - will indicate in column headers`);
    }
    
    // Filter out dimensions without members (but keep typed dimensions)
    const validDimensions = dimensions.filter(dimension => {
      // Keep dimensions with typed members even if they don't have domain members
      if (dimension.typedMember) {
        //console.log(`🔤 Keeping typed dimension: ${dimension.id} with typed member: ${dimension.typedMember.id}`);
        return true;
      }
      // Keep dimensions with domain members
      if (dimension.members && Array.isArray(dimension.members) && dimension.members.length > 0) {
        //console.log(`📂 Keeping domain dimension: ${dimension.id} with ${dimension.members.length} members`);
        return true;
      }
      // Skip dimensions without any members
      //console.log(`⚠️ Skipping dimension ${dimension.id} - no members or typed member defined`);
      return false;
    });

    if (validDimensions.length === 0) {
      //console.log(`⚠️ No valid dimensions with members found, skipping multi-dimensional column generation`);
      return [];
    }
    
    // Get all dimension information
    const dimensionInfos = validDimensions.map(dimension => {
      const axisLabel = dimension.labels.find((l: any) => l.role === 'http://www.xbrl.org/2003/role/label')?.label || dimension.conceptName;
      
      // Handle typed dimensions differently
      if (dimension.typedMember) {
        return {
          id: dimension.id,
          axisLabel,
          isTyped: true,
          typedMemberId: dimension.typedMember.id,
          members: [{
            id: '[typed]',
            label: `${axisLabel} [Typed Input Available]`
          }]
        };
      }
      
      // Handle domain dimensions (existing logic)
      const allMembers = this.getAllDimensionMembers(dimension.members);
      
      return {
        id: dimension.id,
        axisLabel,
        isTyped: false,
        members: allMembers.map(member => ({
          id: member.id,
          label: member.labels.find((l: any) => l.role === 'http://www.xbrl.org/2003/role/label')?.label || member.conceptName
        }))
      };
    });
    
    // Generate all combinations of dimension members
    const combinations = this.generateDimensionCombinations(dimensionInfos);
    //console.log(`📊 Generated ${combinations.length} dimension combinations`);
    // //console.log(`🔍 [DEBUG] Generated combinations:`, combinations.map(combo => ({
    //   memberLabels: combo.map((c: any) => c.memberLabel),
    //   axisIds: combo.map((c: any) => c.axisId),
    //   memberIds: combo.map((c: any) => c.memberId)
    // })));
    
    const columns: FormColumn[] = [];
    
    combinations.forEach((combination: any[], index: number) => {
      // Create column title from combination
      const columnTitle = combination.map((c: any) => c.memberLabel).join(' | ');
      
      // Identify typed and domain members in this combination
      const typedMembers = combination.filter((c: any) => c.isTyped);
      const domainMembers = combination.filter((c: any) => !c.isTyped);
      
      // console.log(`🔍 [Column ${index}] Combination analysis:`, {
      //   totalMembers: combination.length,
      //   typedMembers: typedMembers.length,
      //   domainMembers: domainMembers.length,
      //   hasTypedMembers: typedMembers.length > 0,
      //   columnTitle: columnTitle,
      //   memberLabels: combination.map((c: any) => c.memberLabel),
      //   memberIds: combination.map((c: any) => c.memberId)
      // });
      
      // Create dimension data for this combination
      const dimensionData = {
        dimensionId: `multi_${index}`,
        memberValue: columnTitle, // Required field
        memberLabel: columnTitle, // Required field
        combinations: domainMembers.map((c: any) => ({
          axisId: c.axisId,
          axisLabel: c.axisLabel,
          memberId: c.memberId,
          memberLabel: c.memberLabel
        })),
        // Add typed member information for form fields
        typedMembers: typedMembers.map((c: any) => ({
          axisId: c.axisId,
          axisLabel: c.axisLabel,
          typedMemberId: c.typedMemberId,
          memberLabel: c.memberLabel
        })),
        hasTypedMembers: typedMembers.length > 0,
        memberKey: columnTitle,
        dimensionIdKey: [
          ...domainMembers.map((c: any) => `${c.axisId}|${c.memberId}`),
          ...typedMembers.map((c: any) => `${c.axisId}|[typed]`)
        ].join('::')
      };
      
      // console.log(`🔍 [Column ${index}] Final dimension data:`, {
      //   dimensionId: dimensionData.dimensionId,
      //   memberLabel: dimensionData.memberLabel,
      //   dimensionIdKey: dimensionData.dimensionIdKey,
      //   combinationsCount: dimensionData.combinations?.length,
      //   typedMembersCount: dimensionData.typedMembers?.length
      // });
      
      // Create columns based on period types
      if (periodTypes.size === 0 || (periodTypes.size === 1 && periodTypes.has('duration'))) {
        columns.push({
          id: `duration_${index}`,
          title: `${columnTitle}`,
          description: `Period: ${periodStartDate} to ${periodEndDate}`,
          type: 'dimension',
          dimensionData: {
            ...dimensionData,
            dimensionId: `duration_${index}`
          },
          order: index * 2,
          removable: false
        });
      } else if (periodTypes.size === 1 && periodTypes.has('instant')) {
        columns.push({
          id: `instant_${index}`,
          title: `${columnTitle}`,
          description: `Values as of ${periodStartDate}`,
          type: 'dimension',
          dimensionData: {
            ...dimensionData,
            dimensionId: `instant_${index}`
          },
          order: index * 2,
          removable: false
        });
      } else {
        // Mixed period types - create both duration and instant columns for each combination
        columns.push({
          id: `duration_${index}`,
          title: `${columnTitle}`,
          description: `Period: ${periodStartDate} to ${periodEndDate}`,
          type: 'dimension',
          dimensionData: {
            ...dimensionData,
            dimensionId: `duration_${index}`
          },
          order: index * 2,
          removable: false
        });
        
        columns.push({
          id: `instant_${index}`,
          title: `${columnTitle}`,
          description: `Values as of ${periodStartDate}`,
          type: 'dimension',
          dimensionData: {
            ...dimensionData,
            dimensionId: `instant_${index}`
          },
          order: index * 2 + 1,
          removable: false
        });
      }
    });
    
    //console.log(`📊 Generated ${columns.length} multi-dimensional columns`);
    return columns;
  }

  /**
   * Get all members including children recursively
   */
  private static getAllDimensionMembers(members: any[]): any[] {
    if (!members || !Array.isArray(members)) {
      return [];
    }

    const allMembers: any[] = [];
    
    members.forEach(member => {
      allMembers.push(member);
      
      if (member.children && member.children.length > 0) {
        allMembers.push(...this.getAllDimensionMembers(member.children));
      }
    });
    
    return allMembers;
  }

  /**
   * Generate all combinations of dimension members
   */
  private static generateDimensionCombinations(dimensionInfos: any[]): any[] {
    if (dimensionInfos.length === 0) return [];
    if (dimensionInfos.length === 1) {
      return dimensionInfos[0].members.map((member: any) => [{
        axisId: dimensionInfos[0].id,
        axisLabel: dimensionInfos[0].axisLabel,
        memberId: member.id,
        memberLabel: member.label,
        isTyped: dimensionInfos[0].isTyped || false,
        typedMemberId: dimensionInfos[0].typedMemberId
      }]);
    }
    
    // Generate combinations recursively
    const [firstDimension, ...restDimensions] = dimensionInfos;
    const restCombinations = this.generateDimensionCombinations(restDimensions);
    
    const combinations: any[] = [];
    
    firstDimension.members.forEach((member: any) => {
      if (restCombinations.length === 0) {
        combinations.push([{
          axisId: firstDimension.id,
          axisLabel: firstDimension.axisLabel,
          memberId: member.id,
          memberLabel: member.label,
          isTyped: firstDimension.isTyped || false,
          typedMemberId: firstDimension.typedMemberId
        }]);
      } else {
        restCombinations.forEach(restCombination => {
          combinations.push([
            {
              axisId: firstDimension.id,
              axisLabel: firstDimension.axisLabel,
              memberId: member.id,
              memberLabel: member.label,
              isTyped: firstDimension.isTyped || false,
              typedMemberId: firstDimension.typedMemberId
            },
            ...restCombination
          ]);
        });
      }
    });
    
    return combinations;
  }

  /**
   * Format date for display in column headers
   */
  private static formatDateForDisplay(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  }

  /**
   * Extract entity name from entrypoint URL
   */
  private static extractEntityName(entrypoint: string): string {
    try {
      const url = new URL(entrypoint);
      const pathParts = url.pathname.split('/');
      const filename = pathParts[pathParts.length - 1];
      return filename.replace('.xsd', '').replace(/[^a-zA-Z0-9]/g, ' ').trim() || 'Entity';
    } catch {
      return 'Entity';
    }
  }

  /**
   * Extract role title from role string
   */
  private static extractRoleTitle(role: string): string {
    // Remove role number prefix like "[000001] " and return the title
    const match = role.match(/^\[[\d]+\]\s*(.+)$/);
    return match ? match[1].trim() : role;
  }

  /**
   * Extract role order from role string
   */
  private static extractRoleOrder(role: string): number {
    const match = role.match(/^\[([\d]+)\]/);
    return match ? parseInt(match[1], 10) : 0;
  }

  /**
   * Build columns from XBRL hypercube data (placeholder for now)
   */
  static buildColumnsFromHypercubes(/* _hypercubes: any[] */): any[] {
    // For now, return default column structure
    // This will be expanded when we implement hypercube support
    return [{
      id: 'default',
      label: 'Values',
      type: 'data'
    }];
  }
}
